import { JobProperty, JobTask } from '@prisma/client';
import { Descriptions, Result, Table, Tabs, TabsProps, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { AxiosError } from 'axios';

import { formatIsoDate } from '@app/utils-agnostic';
import { Tables } from '@app/web-components';

import { JobStatusRenderer } from '../atoms';
import { useFindJobById } from '../data';

export type JobViewProps = {
  id: number;
};

export const JobViewPanel = ({ id }: JobViewProps) => {
  const findJobQuery = useFindJobById({
    where: { id },
    include: { tasks: true, properties: true, createdByUser: true, updatedByUser: true },
  });

  const propertyColumns: ColumnsType<JobProperty> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value: object) => <Tables.JsonRenderer json={value} />,
    },
  ];

  const taskColumns: ColumnsType<JobTask> = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 250,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 250,
    },
    {
      title: 'Duration (ms)',
      dataIndex: 'duration',
      width: 250,
      key: 'duration',
    },
    {
      title: 'Error',
      dataIndex: 'failureMessage',
      key: 'failureMessage',
      width: 350,
    },
    {
      title: 'Stack',
      dataIndex: 'failureStacktrace',
      key: 'failureStacktrace',
      width: 600,
      render: (failureStacktrace: string) => <Typography.Text>{failureStacktrace}</Typography.Text>,
    },
    {
      title: 'Configuration',
      dataIndex: 'jsonConfiguration',
      key: 'jsonConfiguration',
      width: 600,
      render: (jsonConfiguration: object) => <Tables.JsonRenderer json={jsonConfiguration} />,
    },
  ];

  const items: TabsProps['items'] = [
    {
      key: 'details',
      label: `Details`,
      children: (
        <Descriptions bordered size="small" layout="horizontal" column={2}>
          <Descriptions.Item label="Name">{findJobQuery.data?.name}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <JobStatusRenderer jobStatus={findJobQuery.data?.status} />
          </Descriptions.Item>
          <Descriptions.Item label="Updated By">{findJobQuery.data?.updatedByUser.name}</Descriptions.Item>
          <Descriptions.Item label="Updated On">
            {findJobQuery.data?.updatedOn ? formatIsoDate(findJobQuery.data?.updatedOn.toString()) : undefined}
          </Descriptions.Item>
          <Descriptions.Item label="Created By">{findJobQuery.data?.createdByUser.name}</Descriptions.Item>
          <Descriptions.Item label="Created On">
            {findJobQuery.data?.createdOn ? formatIsoDate(findJobQuery.data?.createdOn.toString()) : undefined}
          </Descriptions.Item>
        </Descriptions>
      ),
    },
    {
      key: 'diagram',
      label: 'Diagram',
      children: 'Mermaid diagram will go here',
    },
    {
      key: 'properties',
      label: `Properties`,
      children: (
        <Table
          rowKey="id"
          columns={propertyColumns}
          dataSource={findJobQuery.data?.properties}
          size="small"
          pagination={false}
          scroll={{ y: 490 }}
        ></Table>
      ),
    },
    {
      key: 'tasks',
      label: `Tasks`,
      children: (
        <Table
          rowKey="id"
          columns={taskColumns}
          dataSource={findJobQuery.data?.tasks}
          size="small"
          pagination={false}
          scroll={{ y: 490 }}
        ></Table>
      ),
    },
    {
      key: 'log',
      label: 'Log',
      children: 'Log list will go here',
    },
  ];

  if (findJobQuery.error) {
    const error = findJobQuery.error as AxiosError;

    if (error.response?.status === 404) {
      return <Result status="404" title="This job does not exist or was deleted." />;
    }

    return <Result status="error" title="Something went wrong." />;
  }

  if (findJobQuery.isPending) {
    return <div>Loading...</div>;
  }

  return <Tabs defaultActiveKey="details" items={items} />;
};
